/**
 * Keep J/K list-focus index and the focused item's stable id in lockstep.
 *
 * The action-items page remaps keyboard focus after pin/sort by looking up
 * `itemIdRef`. If that ref still points at the previous row when the index
 * has already moved, the remap snaps focus back — then the index-sync effect
 * advances it again, so the highlight flickers between two rows.
 */

/**
 * Write both keyboard-focus refs for the same list index.
 *
 * @param {object} args
 * @param {number} args.index Focus index in `visibleItems`, or -1 for none.
 * @param {object[]} args.visibleItems Currently rendered task rows.
 * @param {(item: object) => string} args.getStableId Stable row id.
 * @param {{ current: number }} args.indexRef Mirrors React state for the index.
 * @param {{ current: string|null }} args.itemIdRef Stable id of the focused row.
 * @returns {number} The index that was written (clamped to the list).
 * @example
 * const indexRef = { current: 0 };
 * const itemIdRef = { current: "a" };
 * syncKeyboardFocusRefs({
 *   index: 1,
 *   visibleItems: [{ id: "a" }, { id: "b" }],
 *   getStableId: (item) => item.id,
 *   indexRef,
 *   itemIdRef,
 * });
 * // indexRef.current === 1, itemIdRef.current === "b"
 */
export const syncKeyboardFocusRefs = ({
  index,
  visibleItems,
  getStableId,
  indexRef,
  itemIdRef,
}) => {
  const items = Array.isArray(visibleItems) ? visibleItems : [];
  let nextIndex = Number(index);
  if (!Number.isFinite(nextIndex)) nextIndex = -1;
  if (items.length === 0 || nextIndex < 0) {
    indexRef.current = items.length === 0 ? -1 : Math.max(-1, nextIndex);
    if (indexRef.current < 0) {
      itemIdRef.current = null;
    }
    if (items.length === 0) {
      indexRef.current = -1;
      itemIdRef.current = null;
    }
    return indexRef.current;
  }
  const clamped = Math.min(nextIndex, items.length - 1);
  indexRef.current = clamped;
  itemIdRef.current = getStableId(items[clamped]);
  return clamped;
};

/**
 * After the visible list changes, resolve where keyboard focus should sit.
 * Prefer the previously focused item id; only fall back to clamping the index.
 *
 * @param {object} args
 * @param {string|null} args.focusId Stable id last assigned to keyboard focus.
 * @param {number} args.currentIndex Index currently in React state.
 * @param {object[]} args.visibleItems Rows after the list change.
 * @param {(item: object) => string} args.getStableId Stable row id.
 * @returns {number} Next focus index, or -1 when the list is empty.
 * @example
 * remapKeyboardFocusAfterListChange({
 *   focusId: "b",
 *   currentIndex: 0,
 *   visibleItems: [{ id: "a" }, { id: "b" }],
 *   getStableId: (item) => item.id,
 * });
 * // 1
 *
 * @example
 * remapKeyboardFocusAfterListChange({
 *   focusId: "b",
 *   currentIndex: 1,
 *   visibleItems: [{ id: "a" }, { id: "b" }],
 *   getStableId: (item) => item.id,
 * });
 * // 1 — same item, no jump
 */
export const remapKeyboardFocusAfterListChange = ({
  focusId,
  currentIndex,
  visibleItems,
  getStableId,
}) => {
  const items = Array.isArray(visibleItems) ? visibleItems : [];
  const prev = Number.isFinite(Number(currentIndex)) ? Number(currentIndex) : -1;
  if (!focusId) {
    if (items.length === 0) return -1;
    if (prev < 0) return prev;
    return Math.min(prev, items.length - 1);
  }
  const nextIndex = items.findIndex((entry) => getStableId(entry) === focusId);
  if (nextIndex >= 0) return nextIndex;
  if (items.length === 0) return -1;
  if (prev < 0) return prev;
  return Math.min(prev, items.length - 1);
};
